import { h, Component } from 'preact';
import cx from 'classnames';
import { from, of, Subscription, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import Axios, { AxiosPromise, AxiosError, AxiosResponse } from 'axios';

import { debounce } from '../../../utils/debounce';

import { LocationSearchResult } from '../../../../types/location-search-result.model';
import { hopplerApi } from '../../../../constants';
import { Location, DivCat, PropertyFilters, ListResponse } from '../../../../types';

import { showToast } from '../../../utils/hoppler-toast.service';
import { locationService } from '../../../utils/location.service';

// import { dataLayer } from '../../../helpers/data-layer-util';

export interface LocationSearchProps {
  onLocationSelected?: (location: Location) => any;
  setClearLocation?: Function;
  setValidation?: Function;

  divCat: DivCat;
  initialText?: string;
  exclude?: string;
  disabled?: boolean;

  extraItemText?: string;
  onExtraItemClick?: () => any;
  onInput?: (input: string) => any;
  level?: 'city' | 'area';

  placeholder?: string;
}

export interface LocationSearchState {
  search: string,
  searchResults: Location[],
  isSearchResultsVisible: boolean,
  isValid: boolean;
  currentLocation: Location,
  currentFilters: PropertyFilters;
  preSale: number;

  searchCopiedFromProps: boolean;
}

export class LocationSearchBox extends Component<LocationSearchProps, LocationSearchState> {
  subscriptions: Subscription[] = [];

  node: HTMLElement;

  private resultsUrl: string = `${hopplerApi.host}/kumita/location/search`;
  private kumitaGenericUrl: string = `${hopplerApi.host}/api/locations/search`;
  
  constructor(props: LocationSearchProps) {
    super(props);

    this.setState({
      searchResults: [],
      isValid: true
    });

    if (props.initialText) {
      this.setState({
        search: props.initialText,
        searchCopiedFromProps: true
      });
    }

    if (props.setValidation) {
      props.setValidation(this.onValidate);
    }

    if (props.setClearLocation) {
      props.setClearLocation(this.clearLocation);
    }
  }

  attachDocumentEventListeners() {
    document.addEventListener('mousedown', this.checkClick, false);
  }

  detachDocumentEventListeners() {
    document.removeEventListener('mousedown', this.checkClick, false);
  }

  onValidate = (isValid) => {
    if (isValid) {
      this.setState({
        isValid: true
      });
    } else {
      this.setState({
        isValid: false
      });
    }
  }

  clearLocation = () => {
    this.setState({
      search: null,
      searchCopiedFromProps: false,
      currentLocation: null
    });
  }

  componentDidMount() {
    this.attachDocumentEventListeners();
  }

  componentWillUnmount() {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
    this.subscriptions = [];

    this.detachDocumentEventListeners();
  }

  checkClick = (event) => {
    // click is outside
    if (!this.node.contains(event.target)) {
      this.setState({
        isSearchResultsVisible: false
      });
    }
  }

  async getSearchResults(searchTerm?: string) {
    let response: AxiosResponse<ListResponse<Location>>;
    const divCat = this.props.divCat;

    try {
      if (this.props.level) {
        response = await locationService.searchWithinLevel(
          this.props.level,
          searchTerm,
          divCat
        );
      } else {
        response = await locationService.search(searchTerm, divCat, 5);
      }

      this.setState({
        searchResults: response.data.results || [], // Backend omits empty arrays
        isSearchResultsVisible: true
      });
    } catch (e) {
      const error = e as AxiosError;
      const message = error.response.data.msg ? error.response.data.msg : '';

      showToast({
        title: 'Failed to retrieve search',
        message: message,
        duration: 3000
      });
    }
  }

  onSearchExecute = debounce((event) => {
    const searchTerm = event.target.value;

    if (searchTerm && searchTerm.length >= 2) {
      this.getSearchResults(searchTerm);
    } else if (!searchTerm || searchTerm.length == 0) {
      this.setState({ isSearchResultsVisible: false });
    }
  }, 500) as (event: Event) => any;

  onSearchInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.setState({
      search: target.value,
      searchCopiedFromProps: false
    });

    if (this.props.onInput) {
      this.props.onInput(target.value);
    }

    this.onSearchExecute(event);
  }

  onSearchInputFocus = (event) => {
    const searchTerm = event.target.value;
    this.getSearchResults(searchTerm);
  }

  onResultClick = (result: Location) => {
    let visibleLabel: string;
    const location: Location = {
      city: result.city,
      name: result.label,
      label: result.label,
      area: result.area,
      building: result.building,
      region: result.region,
      permalink: result.permalink,
      locationPermalink: result.locationPermalink,
      type: result.type,
      level: result.level,
      division: result.division,
      category: result.category,
      count: result.count,
    };

    if (result.level == 'Area') {
      visibleLabel = `${location.area}, ${location.city}`;
    } else {
      visibleLabel = location.label;
    }
    
    this.setState({
      search: visibleLabel,
      searchResults: [],
      isSearchResultsVisible: false,
      currentLocation: location
    });

    // dataLayer.push({
    //   'SearchQuery': result.label,
    //   'Division': result.division,
    //   'PropertyType': result.type,
    //   'Category': result.category,
    //   'event': 'Property Search'
    // });

    if (this.props.onLocationSelected) {
      this.props.onLocationSelected(location);
    }
  }

  getLevelLabel(level: string) {
    if (level === 'Tags') {
      return 'Landmark';
    }

    return level;
  }

  onExtraItemClick(props: LocationSearchProps, state: LocationSearchState) {
    this.setState({ isSearchResultsVisible: false });
    props.onExtraItemClick();
  }

  getExtraItem(props: LocationSearchProps, state: LocationSearchState) {
    if (props.extraItemText) {
      return (
        <p onClick={ () => this.onExtraItemClick(props, state) }>
          <em>{ props.extraItemText }</em>
        </p>
      )
    }
  }

  createResultItem(member: Location, index: number) {
    let displayLabel: string;
    const label = member.label || member.area || member.city;
    displayLabel = label;

    if (!this.props.level) {
      displayLabel = `${displayLabel} (${member.level})`;
    }

    return (
      <p onClick={this.onResultClick.bind(this, member)}>
        { displayLabel }
      </p>
    )
  }

  render(props: LocationSearchProps, state: LocationSearchState) {
    return (
      <div className="hi-location-search-box" ref={node => this.node = node}> 
        <input 
          disabled={ props.disabled }
          className={ cx('hi-location-search-box__input form-control', {'invalid': !state.isValid}) } 
          type="text"
          placeholder={ props.placeholder || 'Search by city, neighborhood, or building' }
          value={state.search}
          onInput={ event => this.onSearchInputChange(event) }
        />
        <span className="hi-location-search-box__icon">
          <i className="fas fa-search"></i>
        </span>
        <div className={cx('hi-location-search-box__results', {active : state.isSearchResultsVisible})}>
          { this.state.searchResults.map((member, i) => this.createResultItem(member, i)) }

          { this.getExtraItem(props, state) }
        </div>
      </div>
    );
  }

  componentWillReceiveProps(nextProps: LocationSearchProps) {
    const curProps = this.props;

    // Allow the value to be reset if it's changed by user input and the props
    // are updated
    if (curProps.initialText != nextProps.initialText || !this.state.searchCopiedFromProps) {
      this.setState({
        search: nextProps.initialText,
        searchCopiedFromProps: true
      });
    }
  }
}